import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getDoc,
  doc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { db, storage } from "../../services/firebase";
import { getBlob, ref } from "firebase/storage";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  saveBlob,
  triggerUrlDownload,
  resolveStorageDownloadUrl,
  getPhotoBlob,
  downloadSinglePhoto,
  getDocketSource,
  openOrDownloadStoredFile,
  getConfirmationSource,
} from "./viewHistory/fileDownloads";
import {
  FUTILE_CHARGE_AMOUNT,
  linkify,
  addrToText,
  durationFromTrip,
  hasValue,
  firstDefined,
  toNumber,
  fmtCurrency,
  formatExportDate,
  csvEscape,
  driverLabel,
  getRunVehicleFields,
  pickPreStartStart,
  pickPreStartEnd,
  buildJobHref,
  getRunSortNumber,
  mergeAssignments,
} from "./viewHistory/helpers";
import { inferServiceQty, getDateRangeArray } from "./viewHistory/serviceQty";

/* =========================
   MAIN: ViewHistoryTab
   ========================= */
const DEFAULT_EXPORT_COLUMNS = [
  "Customer",
  "Date",
  "Address",
  "Service",
  "QtyBinsCollected",
  "UnitAmount",
  "LineTotal",
  "FutileCharge",
  "FutileNote",
  "DriverNotes",
];

const PRESTART_LABELS = Object.fromEntries(
  [
    ["engineOil", "Engine Oil / Hydraulic / Auxiliary oil level OK"],
    ["tyres", "Tyres: visual integrity, wear & pressure"],
    ["wheelSecurity", "Wheel security (rims & nut indicators) OK"],
    ["coolant", "Coolant / water levels OK"],
    ["seatbelt", "Seatbelt & seat operational & condition OK"],
    ["airLines", "Air lines checked"],
    ["lights", "All lights/reflectors/lenses & indicators operational"],
    ["beacons", "Beacons & reverse lights (or buzzer) working"],
    ["mirrorsWindows", "Mirrors/windows/windscreen condition OK"],
    ["leaks", "No visible leaks (coolant, oil, fuel, air, other)"],
    ["lifterCompactor", "Lifter/Compactor/Tank/Pump unit OK (if fitted)"],
    ["damagePanels", "Damage: panels/structural members secure"],
    ["kits", "Spill kits, first aid kits & safety triangles present/in date"],
    ["extinguisher", "Fire extinguisher secure and in date"],
    ["eStops", "E-Stops checked (all)"],
    ["scales", "Load scales/weighers checked & zero"],
    ["numberPlate", "Number plate secure/visible & light operational"],
    ["placards", "Signage/placards (road transport & WHS) displayed"],
    ["wipers", "Windscreen wipers/washers operational"],
    ["horn", "Horn operational"],
    ["radio", "Communication radio operational"],
    ["cameras", "Camera(s) operational"],
    ["brakeGauges", "Brake gauges operational"],
    ["smartBrake", "Smart Brake operational"],
    ["parkBrake", "Park Brake operational"],
    ["footBrake", "Foot Brake rolling test carried out"],
  ].map(([k, v]) => [k, v])
);

function getFaultsForPrestart(p) {
  if (!p?.checklist) return [];
  return Object.entries(p.checklist)
    .filter(([, v]) => v === "fault")
    .map(([k]) => ({
      key: k,
      label: PRESTART_LABELS[k] || k,
      comment: p?.comments?.[k] || "",
    }));
}

export default function ViewHistoryTab({ drivers = [], companies = [] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = new URLSearchParams(location.search || "");

  const todayAU = new Date().toLocaleDateString("en-CA", {
    timeZone: "Australia/Melbourne",
  });
  const initialDate = qs.get("date") || todayAU;
  const initialDriverId = qs.get("driverId") || "";
  const selectedRunKey = qs.get("runKey") || "";

  const [selectedDriverId, setSelectedDriverId] = useState(initialDriverId);
  const [date, setDate] = useState(initialDate);
  const [exportFromDate, setExportFromDate] = useState(initialDate);
  const [exportToDate, setExportToDate] = useState(initialDate);
  const [groupExportBySite, setGroupExportBySite] = useState(true);
  const [selectedExportColumns, setSelectedExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  const [advancedExportOpen, setAdvancedExportOpen] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverPickerOpen, setDriverPickerOpen] = useState(false);
  const [prestartsSectionOpen, setPrestartsSectionOpen] = useState(false);
  const [postTripSectionOpen, setPostTripSectionOpen] = useState(false);

  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverEmail, setDriverEmail] = useState("");
  const [zipping, setZipping] = useState(false);
  const [prestartsById, setPrestartsById] = useState({});
  const [weighbridgeDockets, setWeighbridgeDockets] = useState({});
  const [postTrip, setPostTrip] = useState(null);
  const [postTripsById, setPostTripsById] = useState({});
  const [jobNotesById, setJobNotesById] = useState({});
  const [confirmDownloadingJobId, setConfirmDownloadingJobId] = useState(null);
  const [confirmSourceByJobId, setConfirmSourceByJobId] = useState({});
  const [docketSourceByJobId, setDocketSourceByJobId] = useState({});
  const [signedDocketSourceByJobId, setSignedDocketSourceByJobId] = useState({});
  const [assignmentsForDate, setAssignmentsForDate] = useState([]);
  const runCardRefs = useRef({});

  const EXPORT_COLUMN_OPTIONS = [
    ["Customer", "Customer"],
    ["Date", "Date"],
    ["Address", "Address"],
    ["Site", "Site"],
    ["Run", "Run"],
    ["Service", "Service"],
    ["QtyBinsCollected", "Qty (bins collected)"],
    ["UnitAmount", "Unit amount"],
    ["LineTotal", "Line total"],
    ["DriverNotes", "Driver notes / description"],
    ["Description", "Description"],
    ["PONumber", "PO number"],
    ["ETA", "ETA"],
    ["StartTrip", "Job start trip"],
    ["JobStart", "Job start"],
    ["Finished", "Finished"],
    ["Duration", "Duration"],
    ["UploadedBy", "Uploaded by"],
    ["ProofPhoto", "Proof photo"],
    ["SiteContact", "Site contact"],
    ["PhoneNumber", "Phone number"],
    ["BreakDetails", "Break details"],
    ["PreStartStart", "Pre-start start"],
    ["PreStartEnd", "Pre-start end"],
    ["PreStartAt", "Pre-start at"],
    ["Skipped", "Skipped"],
    ["SkipReason", "Skip reason"],
    ["SkipNote", "Skip note"],
    ["SkippedAt", "Skipped at"],
    ["SkippedBy", "Skipped by"],
    ["Futile", "Futile"],
    ["FutileCharge", "Futile charge"],
    ["FutileNote", "Futile note"],
    ["PostTripOdoEnd", "Post trip odometer end"],
    ["PostTripEndTime", "Post trip end time"],
    ["PostTripNotes", "Post trip notes"],
  ];



  const selectedDriver = useMemo(
    () => drivers.find((d) => d.uid === selectedDriverId),
    [drivers, selectedDriverId]
  );

  const companyNameById = useMemo(() => {
    const map = {};
    (companies || []).forEach((company) => {
      map[company.id] = company.name || company.id;
    });
    return map;
  }, [companies]);

  useEffect(() => {
    if (!date) {
      setAssignmentsForDate([]);
      return;
    }

    let ignore = false;
    (async () => {
      try {
        const q = query(collection(db, "assignments"), where("date", "==", date));
        const snaps = await getDocs(q);
        if (!ignore) {
          setAssignmentsForDate(
            snaps.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          );
        }
      } catch (err) {
        console.warn("Failed to load assignments for rego search:", err);
        if (!ignore) setAssignmentsForDate([]);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [date]);

  const historySearchOptions = useMemo(() => {
    const driverMap = new Map((drivers || []).map((driver) => [driver.uid, driver]));
    const options = [];

    (drivers || []).forEach((driver) => {
      const companyIds = [
        driver.companyId,
        ...(Array.isArray(driver.companyIds) ? driver.companyIds : []),
      ].filter(Boolean);
      const companyText = companyIds
        .map((companyId) => `${companyId} ${companyNameById[companyId] || ""}`)
        .join(" ");

      options.push({
        key: `driver-${driver.uid}`,
        type: "driver",
        driverId: driver.uid,
        primary: driverLabel(driver),
        secondary: driver.uid || "",
        searchText: `${driverLabel(driver)} ${driver.uid || ""} ${companyText}`,
      });
    });

    assignmentsForDate.forEach((assignmentDoc) => {
      const driverId = assignmentDoc.driverId || "";
      if (!driverId) return;
      const driver = driverMap.get(driverId) || { uid: driverId };
      Object.entries(assignmentDoc.runs || {}).forEach(([runKey, list]) => {
        const vehicle = getRunVehicleFields(assignmentDoc, runKey, list);
        if (!vehicle.vehicleRego && !vehicle.vehicleLabel) return;

        const companyId =
          assignmentDoc.runMeta?.[runKey]?.companyId ||
          (Array.isArray(list) && list.find(Boolean)?.companyId) ||
          assignmentDoc.companyId ||
          "";
        const companyText = `${companyId} ${companyNameById[companyId] || ""}`.trim();
        const primary = vehicle.vehicleLabel || vehicle.vehicleRego;
        options.push({
          key: `truck-${assignmentDoc.id}-${runKey}`,
          type: "truck",
          driverId,
          runKey,
          primary,
          secondary: `${runKey} • ${driverLabel(driver)}`,
          searchText: `${primary} ${vehicle.vehicleRego} ${vehicle.vehicleLoadType} ${runKey} ${driverLabel(driver)} ${driverId} ${companyText}`,
        });
      });
    });

    const term = driverSearch.trim().toLowerCase();
    return term
      ? options.filter((option) => option.searchText.toLowerCase().includes(term))
      : options;
  }, [assignmentsForDate, companyNameById, driverSearch, drivers]);

  const selectHistorySearchOption = (option) => {
    setSelectedDriverId(option.driverId);
    setDriverSearch(option.primary);
    setDriverPickerOpen(false);

    const p = new URLSearchParams(location.search || "");
    p.set("tab", "view-history");
    if (date) p.set("date", date);
    p.set("driverId", option.driverId);
    if (option.runKey) p.set("runKey", option.runKey);
    else p.delete("runKey");
    navigate({ pathname: location.pathname, search: `?${p.toString()}` });
  };

  const inferredCompanyId = useMemo(() => {
    if (!selectedDriver) return null;
    if (selectedDriver.companyId) return selectedDriver.companyId;
    if (
      Array.isArray(selectedDriver.companyIds) &&
      selectedDriver.companyIds.length
    ) {
      return selectedDriver.companyIds[0];
    }
    return null;
  }, [selectedDriver]);

  const companySlug = useMemo(() => {
    if (!inferredCompanyId) return "company";
    const c = companies.find((x) => x.id === inferredCompanyId);
    return c
      ? (c.name || c.id).toLowerCase().replace(/[^\w]+/g, "-")
      : inferredCompanyId;
  }, [companies, inferredCompanyId]);

  const toSafeRunKey = (value = "") =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");


  const runSummaries = useMemo(() => {
    if (!assignment?.runs) return [];

    return Object.entries(assignment.runs || {})
      .map(([runKey, addresses]) => {
        const list = Array.isArray(addresses) ? addresses.filter(Boolean) : [];
        const firstJob =
          list.find((item) => item && !item.isWeighbridgeDocket) ||
          list[0] ||
          null;
        const meta = assignment?.runMeta?.[runKey] || {};

        const companyIdValue =
          meta.companyId || firstJob?.companyId || assignment?.companyId || "";
        const companyNameValue =
          meta.companyName ||
          firstJob?.companyName ||
          (companyIdValue && companyIdValue !== "mixed" ? companyIdValue : "") ||
          "";
        const uiVariantValue =
          meta.uiVariant || firstJob?.uiVariant || assignment?.uiVariant || "";
        const isGreenspaceVehicleRun =
          String(uiVariantValue || "").toLowerCase() === "greenspace";
        const vehicle = getRunVehicleFields(assignment, runKey, list);
        const vehicleIdValue = meta.vehicleId || firstJob?.vehicleId || "";
        const vehicleRegoValue = vehicle.vehicleRego;
        const vehicleLoadTypeValue = vehicle.vehicleLoadType;
        const vehicleLabelValue =
          meta.vehicleLabel ||
          firstJob?.vehicleLabel ||
          [vehicleRegoValue, vehicleLoadTypeValue].filter(Boolean).join(" • ");
        const prestartDocId = isGreenspaceVehicleRun
          ? `${selectedDriverId || "driver"}_${date}_${toSafeRunKey(runKey)}`
          : companyIdValue
            ? `${companyIdValue}_${selectedDriverId || "driver"}_${date}`
            : `${selectedDriverId || "driver"}_${date}`;

        return {
          runKey,
          list,
          meta,
          companyId: companyIdValue,
          companyName: companyNameValue,
          uiVariant: uiVariantValue,
          isGreenspaceVehicleRun,
          vehicleId: vehicleIdValue,
          vehicleRego: vehicleRegoValue,
          vehicleLoadType: vehicleLoadTypeValue,
          vehicleLabel: vehicleLabelValue,
          prestartDocId,
          postTripDocId: vehicleIdValue || vehicleRegoValue || isGreenspaceVehicleRun
            ? `${selectedDriverId || "driver"}_${date}_${toSafeRunKey(runKey)}`
            : prestartDocId,
        };
      })
      .sort((a, b) => getRunSortNumber(a.runKey) - getRunSortNumber(b.runKey));
  }, [assignment, selectedDriverId, date]);

  const prestartList = useMemo(() => {
    const seen = new Set();
    return runSummaries
      .map((run) => {
        const data = prestartsById?.[run.prestartDocId] || null;
        if (seen.has(run.prestartDocId)) return null;
        seen.add(run.prestartDocId);
        return {
          ...run,
          data,
          startedAtLabel: pickPreStartStart(data),
          completedAtLabel: pickPreStartEnd(data),
        };
      })
      .filter(Boolean);
  }, [runSummaries, prestartsById]);

  const primaryPrestart = prestartList[0]?.data || null;
  const selectedTruckLabel =
    (selectedRunKey
      ? runSummaries.find((run) => run.runKey === selectedRunKey)?.vehicleLabel
      : runSummaries.find((run) => run.vehicleLabel)?.vehicleLabel) || "";

  const runPostTripEntries = useMemo(
    () =>
      runSummaries
        .map((run) => ({
          run,
          data:
            postTripsById?.[run.postTripDocId] ||
            postTripsById?.[run.prestartDocId] ||
            null,
        }))
        .filter((entry) => entry.data),
    [postTripsById, runSummaries]
  );

  const selectedRunPostTrip =
    (selectedRunKey
      ? runPostTripEntries.find((entry) => entry.run.runKey === selectedRunKey)
      : runPostTripEntries[0]) || null;

  const postTripSummary = postTrip || selectedRunPostTrip?.data || null;

  useEffect(() => {
    const p = new URLSearchParams(location.search || "");
    p.set("tab", "view-history");
    if (date) p.set("date", date);
    else p.delete("date");
    if (selectedDriverId) p.set("driverId", selectedDriverId);
    else p.delete("driverId");
    const next = `?${p.toString()}`;
    if (next !== location.search) {
      navigate(
        { pathname: location.pathname, search: next },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedDriverId]);

  useEffect(() => {
    if (!assignment || !selectedRunKey) return;

    const frame = requestAnimationFrame(() => {
      const card = runCardRefs.current[selectedRunKey];
      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "start" });
        card.focus({ preventScroll: true });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [assignment, selectedRunKey]);

  const downloadConfirmationPdfForJob = async (jobId) => {
    if (!jobId) return;

    setConfirmDownloadingJobId(jobId);
    try {
      let src = confirmSourceByJobId[jobId];
      if (!src) {
        const snap = await getDoc(doc(db, "jobs", jobId));
        if (!snap.exists()) {
          alert("Job not found.");
          return;
        }

        src = getConfirmationSource(snap.data());

        if (!src?.url && !src?.path) {
          const trySub = async (subName) => {
            try {
              const q = query(
                collection(db, "jobs", jobId, subName),
                orderBy("createdAt", "desc"),
                limit(1)
              );
              const s = await getDocs(q);
              if (!s.empty) {
                const data = s.docs[0].data();
                return {
                  url: data.pdfUrl || data.url || "",
                  path: data.pdfPath || data.path || "",
                };
              }
            } catch {
              //
            }
            return { url: "", path: "" };
          };

          const c = await trySub("clientConfirmations");
          src = c.url || c.path ? c : await trySub("customerConfirmations");
        }

        setConfirmSourceByJobId((prev) => ({ ...prev, [jobId]: src }));
      }

      if (!src?.url && !src?.path) {
        alert("No service confirmation PDF found for this job yet.");
        return;
      }

      const filename = `service-confirmation_${jobId}.pdf`;

      try {
        const resolvedUrl = await resolveStorageDownloadUrl(src);
        if (resolvedUrl) {
          triggerUrlDownload(resolvedUrl, filename);
          return;
        }
      } catch (err) {
        console.warn("Direct confirmation download fallback failed:", err);
      }

      if (src.path) {
        const blob = await getBlob(ref(storage, src.path));
        saveBlob(blob, filename);
        return;
      }

      window.open(src.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Download confirmation failed:", err);
      alert("Failed to download confirmation PDF.");
    } finally {
      setConfirmDownloadingJobId(null);
    }
  };

  const viewJobDocket = async (jobId, signed = false) => {
    if (!jobId) return;
    try {
      const cached = signed
        ? signedDocketSourceByJobId[jobId]
        : docketSourceByJobId[jobId];

      let source = cached;
      if (!source?.url && !source?.path) {
        const snap = await getDoc(doc(db, "jobs", jobId));
        if (!snap.exists()) {
          alert("Job not found.");
          return;
        }
        const data = snap.data() || {};
        source = getDocketSource(data, signed);
        if (signed) {
          setSignedDocketSourceByJobId((prev) => ({ ...prev, [jobId]: source }));
        } else {
          setDocketSourceByJobId((prev) => ({ ...prev, [jobId]: source }));
        }
      }

      if (!source?.url && !source?.path) {
        alert(
          signed
            ? "No signed docket found for this job yet."
            : "No docket uploaded for this job."
        );
        return;
      }

      await openOrDownloadStoredFile(
        source,
        source?.name || `${signed ? "signed-docket" : "docket"}_${jobId}`
      );
    } catch (err) {
      console.error("Failed to open docket:", err);
      alert("Failed to open docket file.");
    }
  };

  const hasJobDocket = (jobId) =>
    !!(
      jobId &&
      (docketSourceByJobId[jobId]?.url || docketSourceByJobId[jobId]?.path)
    );

  const hasSignedJobDocket = (jobId) =>
    !!(
      jobId &&
      (signedDocketSourceByJobId[jobId]?.url ||
        signedDocketSourceByJobId[jobId]?.path)
    );

  const fetchAssignment = async () => {
    if (!selectedDriverId || !date) return;

    setLoading(true);
    try {
      const legacyId = `${selectedDriverId}_${date}`;
      const scopedId = inferredCompanyId
        ? `${inferredCompanyId}_${selectedDriverId}_${date}`
        : null;

      const userSnap = await getDoc(doc(db, "users", selectedDriverId));
      setDriverEmail(
        userSnap.exists() ? userSnap.data().email || "unknown" : "unknown"
      );

      let mergedAssignment = null;

      try {
        const q = query(
          collection(db, "assignments"),
          where("driverId", "==", selectedDriverId),
          where("date", "==", date)
        );
        const snaps = await getDocs(q);
        const docs = snaps.docs.map((d) => d.data()).filter(Boolean);
        mergedAssignment = mergeAssignments(docs);
      } catch (err) {
        console.warn("Assignments query fallback triggered:", err);
      }

      if (!mergedAssignment) {
        const fallbackDocs = [];

        if (scopedId) {
          const s = await getDoc(doc(db, "assignments", scopedId));
          if (s.exists()) fallbackDocs.push(s.data());
        }

        const legacySnap = await getDoc(doc(db, "assignments", legacyId));
        if (legacySnap.exists()) fallbackDocs.push(legacySnap.data());

        mergedAssignment = mergeAssignments(fallbackDocs);
      }

      setAssignment(mergedAssignment);

      const buildRunContext = (runKey, list = []) => {
        const firstJob = Array.isArray(list)
          ? list.find((item) => item && !item.isWeighbridgeDocket) ||
            list.find(Boolean) ||
            null
          : null;
        const meta = mergedAssignment?.runMeta?.[runKey] || {};
        const companyIdValue =
          meta.companyId || firstJob?.companyId || mergedAssignment?.companyId || "";
        const uiVariantValue =
          meta.uiVariant || firstJob?.uiVariant || mergedAssignment?.uiVariant || "";
        const isGreenspaceVehicleRun =
          String(uiVariantValue || "").toLowerCase() === "greenspace";
        const prestartDocId = isGreenspaceVehicleRun
          ? `${selectedDriverId || "driver"}_${date}_${toSafeRunKey(runKey)}`
          : companyIdValue
            ? `${companyIdValue}_${selectedDriverId || "driver"}_${date}`
            : `${selectedDriverId || "driver"}_${date}`;
        return { prestartDocId, companyIdValue };
      };

      const prestartIds = new Set();
      prestartIds.add(legacyId);
      if (scopedId) prestartIds.add(scopedId);
      if (mergedAssignment?.runs) {
        Object.entries(mergedAssignment.runs).forEach(([runKey, addresses]) => {
          const ctx = buildRunContext(runKey, addresses);
          if (ctx?.prestartDocId) prestartIds.add(ctx.prestartDocId);
        });
      }

      const preEntries = await Promise.all(
        Array.from(prestartIds)
          .filter(Boolean)
          .map(async (id) => {
            try {
              const s = await getDoc(doc(db, "prestarts", id));
              return [id, s.exists() ? s.data() : null];
            } catch {
              return [id, null];
            }
          })
      );

      const preMap = {};
      preEntries.forEach(([id, data]) => {
        if (data) preMap[id] = data;
      });
      setPrestartsById(preMap);

      try {
        const ptSnap = await getDoc(doc(db, "postTrips", legacyId));
        setPostTrip(ptSnap.exists() ? ptSnap.data() : null);
      } catch (e) {
        console.error("Failed to load post trip:", e);
        setPostTrip(null);
      }

      const postTripIds = new Set([legacyId]);
      if (mergedAssignment?.runs) {
        Object.entries(mergedAssignment.runs).forEach(([runKey, addresses]) => {
          const list = Array.isArray(addresses) ? addresses.filter(Boolean) : [];
          const vehicle = getRunVehicleFields(mergedAssignment, runKey, list);
          const meta = mergedAssignment?.runMeta?.[runKey] || {};
          const firstJob =
            list.find((item) => item && !item.isWeighbridgeDocket) || list[0] || null;
          const uiVariantValue =
            meta.uiVariant || firstJob?.uiVariant || mergedAssignment?.uiVariant || "";
          const hasVehicle = !!(vehicle.vehicleRego || vehicle.vehicleLoadType || vehicle.vehicleLabel);
          const isGreenspaceVehicleRun =
            String(uiVariantValue || "").toLowerCase() === "greenspace";
          const ctx = buildRunContext(runKey, addresses);
          const postTripDocId =
            hasVehicle || isGreenspaceVehicleRun
              ? `${selectedDriverId || "driver"}_${date}_${toSafeRunKey(runKey)}`
              : ctx?.prestartDocId;
          if (postTripDocId) postTripIds.add(postTripDocId);
        });
      }

      const postEntries = await Promise.all(
        Array.from(postTripIds)
          .filter(Boolean)
          .map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "postTrips", id));
              return [id, snap.exists() ? snap.data() : null];
            } catch {
              return [id, null];
            }
          })
      );
      const postMap = {};
      postEntries.forEach(([id, data]) => {
        if (data) postMap[id] = data;
      });
      setPostTripsById(postMap);

      if (mergedAssignment?.runs && selectedDriverId && date) {
        const entries = await Promise.all(
          Object.entries(mergedAssignment.runs).map(async ([runKey, addresses]) => {
            const ctx = buildRunContext(runKey, addresses);
            const docketCompanyId =
              ctx.companyIdValue || mergedAssignment.companyId || inferredCompanyId || "default";
            const docketId = `${docketCompanyId}_${selectedDriverId}_${date}_${runKey}`;
            try {
              const snap = await getDoc(doc(db, "weighbridgeDockets", docketId));
              return [runKey, snap.exists() ? snap.data() : null];
            } catch (e) {
              console.error("Failed to load weighbridge docket:", e);
              return [runKey, null];
            }
          })
        );

        const map = {};
        entries.forEach(([rk, d]) => {
          if (d) map[rk] = d;
        });
        setWeighbridgeDockets(map);
      } else {
        setWeighbridgeDockets({});
      }

      if (mergedAssignment?.runs) {
        const jobIds = [];
        Object.values(mergedAssignment.runs).forEach((addresses) => {
          (addresses || []).forEach((item) => {
            if (item?.isWeighbridgeDocket) return;
            if (item?.jobId && !jobIds.includes(item.jobId)) {
              jobIds.push(item.jobId);
            }
          });
        });

        if (jobIds.length) {
          const notes = {};
          const docketCache = {};
          const signedCache = {};
          await Promise.all(
            jobIds.map(async (id) => {
              try {
                const s = await getDoc(doc(db, "jobs", id));
                if (s.exists()) {
                  const data = s.data();
                  notes[id] = data.driverNotes || "";
                  docketCache[id] = getDocketSource(data, false);
                  signedCache[id] = getDocketSource(data, true);
                }
              } catch (e) {
                console.error("Failed to load job note:", e);
              }
            })
          );
          setJobNotesById(notes);
          setDocketSourceByJobId(docketCache);
          setSignedDocketSourceByJobId(signedCache);
        } else {
          setJobNotesById({});
          setDocketSourceByJobId({});
          setSignedDocketSourceByJobId({});
        }
      } else {
        setJobNotesById({});
        setDocketSourceByJobId({});
        setSignedDocketSourceByJobId({});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDriverId && date) {
      fetchAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (!selectedDriverId || !date) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAssignment();
    }, 150);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverId, date]);

  const toggleExportColumn = (key) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const getServiceExportDetails = (
    item = {},
    jobData = {},
    loadDocs = [],
    serviceCatalog = []
  ) => {
    const explicitServiceId = firstDefined(
      jobData.serviceId,
      jobData.jobTypeId,
      jobData?.service?.id,
      jobData?.jobType?.id,
      item.serviceId,
      item.jobTypeId
    );

    const service = firstDefined(
      jobData.serviceName,
      jobData.selectedServiceName,
      jobData.serviceLabel,
      jobData.service?.name,
      jobData.service?.label,
      jobData.service?.title,
      jobData.jobTypeLabel,
      jobData.jobTypeName,
      jobData.jobType?.name,
      jobData.jobType?.label,
      jobData.jobType?.title,
      typeof jobData.jobType === "string" ? jobData.jobType : "",
      item.serviceName,
      item.selectedServiceName,
      item.serviceLabel,
      item.service?.name,
      item.service?.label,
      item.service?.title,
      typeof item.service === "string" ? item.service : "",
      item.jobTypeLabel,
      item.jobTypeName,
      item.jobType?.name,
      item.jobType?.label,
      item.jobType?.title,
      typeof item.jobType === "string" ? item.jobType : "",
      item.type,
      jobData.type
    ) || "";

    const serviceCode = String(
      firstDefined(
        jobData.serviceCode,
        jobData.jobTypeCode,
        jobData.service?.code,
        jobData.jobType?.code,
        item.serviceCode,
        item.jobTypeCode,
        item.service?.code,
        item.jobType?.code,
        ""
      ) || ""
    )
      .trim()
      .toLowerCase();

    const matchedService =
      (explicitServiceId && serviceCatalog.find((svc) => svc.id === explicitServiceId)) ||
      serviceCatalog.find((svc) => {
        const svcName = String(firstDefined(svc?.name, svc?.label, svc?.title, "") || "")
          .trim()
          .toLowerCase();
        return svcName && svcName === String(service || "").trim().toLowerCase();
      }) ||
      (serviceCode &&
        serviceCatalog.find((svc) =>
          [
            svc?.code,
            svc?.serviceCode,
            svc?.jobTypeCode,
            svc?.slug,
          ]
            .filter(hasValue)
            .map((value) => String(value).trim().toLowerCase())
            .includes(serviceCode)
        )) ||
      null;

    const unitAmountRaw = firstDefined(
      jobData.servicePrice,
      jobData.serviceUnitPrice,
      jobData.unitAmount,
      jobData.pricePerBin,
      jobData.price,
      jobData.rate,
      jobData?.serviceDetails?.price,
      jobData?.serviceDetails?.unitAmount,
      jobData?.serviceDetails?.pricePerBin,
      matchedService?.price,
      matchedService?.unitAmount,
      matchedService?.pricePerBin,
      matchedService?.servicePrice,
      item.servicePrice,
      item.serviceUnitPrice,
      item.unitAmount,
      item.pricePerBin,
      item.price,
      item.rate
    );

    const qty = inferServiceQty(item, jobData, loadDocs);
    const unitAmount = toNumber(unitAmountRaw);
    const safeQty = toNumber(qty);
    const lineTotal = unitAmount > 0 && safeQty > 0 ? unitAmount * safeQty : 0;
    const serviceLabel =
      firstDefined(matchedService?.name, matchedService?.label, matchedService?.title, service) || "";
    return { service: serviceLabel, qty, unitAmount, lineTotal, matchedService };
  };

  const fetchAssignmentsForExport = async () => {
    if (!selectedDriverId || !exportFromDate || !exportToDate) return [];

    let docs = [];
    try {
      const q = query(
        collection(db, "assignments"),
        where("driverId", "==", selectedDriverId),
        where("date", ">=", exportFromDate),
        where("date", "<=", exportToDate),
        orderBy("date", "asc")
      );
      const snaps = await getDocs(q);
      docs = snaps.docs.map((d) => d.data()).filter(Boolean);
    } catch (err) {
      console.warn("Export assignments query fallback triggered:", err);
    }

    if (!docs.length) {
      const byDate = {};
      const dateKeys = getDateRangeArray(exportFromDate, exportToDate);
      await Promise.all(
        dateKeys.map(async (dateKey) => {
          try {
            const snap = await getDoc(doc(db, "assignments", `${selectedDriverId}_${dateKey}`));
            if (!snap.exists()) return;
            const data = snap.data() || {};
            byDate[dateKey] = byDate[dateKey] || [];
            byDate[dateKey].push({ ...data, date: data?.date || dateKey });
          } catch (err) {
            console.warn("Export assignment doc-id fallback failed:", dateKey, err);
          }
        })
      );
      docs = Object.values(byDate).flat();
    }

    if (!docs.length && exportFromDate === exportToDate && assignment) {
      return [{ date: exportFromDate, data: assignment }];
    }

    const grouped = docs.reduce((acc, item) => {
      const key = item?.date || exportFromDate;
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([dateKey, list]) => ({ date: dateKey, data: mergeAssignments(list) }))
      .filter((entry) => entry.data)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  };

  const exportAdvancedCSV = async () => {
    const emailPrefix = (driverEmail.split("@")[0] || "driver").replace(
      /[^\w.-]/g,
      "_"
    );
    if (!selectedDriverId || !exportFromDate || !exportToDate) {
      alert("Please select driver and export date range.");
      return;
    }
    if (selectedExportColumns.length === 0) {
      alert("Please select at least one export column.");
      return;
    }

    const assignmentEntries = await fetchAssignmentsForExport();
    if (!assignmentEntries.length) {
      alert("No jobs found for the selected export range.");
      return;
    }

    const allJobIds = new Set();
    assignmentEntries.forEach(({ data }) => {
      Object.values(data?.runs || {}).forEach((addresses) => {
        (addresses || []).forEach((item) => {
          if (item?.jobId && !item?.isWeighbridgeDocket) allJobIds.add(item.jobId);
        });
      });
    });

    const jobsMap = {};
    const loadsMap = {};
    let serviceCatalog = [];
    try {
      const servicesSnap = await getDocs(collection(db, "jobTypes"));
      serviceCatalog = servicesSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
    } catch (err) {
      console.warn("Failed to load services for advanced export:", err);
      serviceCatalog = [];
    }
    await Promise.all(
      Array.from(allJobIds).map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "jobs", id));
          jobsMap[id] = snap.exists() ? snap.data() || {} : {};
        } catch {
          jobsMap[id] = {};
        }
        try {
          const loadsSnap = await getDocs(
            query(collection(db, "loads"), where("jobId", "==", id))
          );
          loadsMap[id] = loadsSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        } catch {
          loadsMap[id] = [];
        }
      })
    );

    const rows = [];
    for (const { date: assignmentDate, data } of assignmentEntries) {
      const breaksArr = Array.isArray(data?.breaks) ? data.breaks : [];
      const breakDetails = breaksArr
        .map((b, idx) => {
          if (!b) return "";
          const s = b.start || "";
          const e = b.end || "";
          if (!s && !e) return "";
          return `#${idx + 1} ${s || "?"} - ${e || "?"}`;
        })
        .filter(Boolean)
        .join(" | ");

      let postTripDoc = null;
      try {
        const ptSnap = await getDoc(doc(db, "postTrips", `${selectedDriverId}_${assignmentDate}`));
        postTripDoc = ptSnap.exists() ? ptSnap.data() : null;
      } catch {
        postTripDoc = null;
      }

      const runEntries = Object.entries(data?.runs || {}).sort(
        ([a], [b]) => getRunSortNumber(a) - getRunSortNumber(b)
      );

      runEntries.forEach(([runKey, addresses]) => {
        const firstJob = (addresses || []).find((item) => item && !item.isWeighbridgeDocket) || null;
        const customerName =
          firstDefined(
            data?.runMeta?.[runKey]?.companyName,
            firstJob?.companyName,
            firstJob?.customer,
            firstJob?.customerName,
            ""
          ) || "";

        (addresses || []).forEach((item) => {
          if (!item || item.isWeighbridgeDocket) return;
          const jobData = item?.jobId ? jobsMap[item.jobId] || {} : {};
          const jobLoads = item?.jobId ? loadsMap[item.jobId] || [] : [];
          const svc = getServiceExportDetails(item, jobData, jobLoads, serviceCatalog);
          const driverNotes = firstDefined(jobData.driverNotes, item.driverNotes, "") || "";
          const description = firstDefined(item.description, jobData.description, "") || "";
          const siteName =
            firstDefined(
              item.siteLabel,
              item.siteName,
              jobData.siteLabel,
              jobData.siteName,
              addrToText(item.address)
            ) || "";
          const dateLabel = formatExportDate(item.date || jobData.date || assignmentDate, assignmentDate);
          const lineTotalLabel = svc.lineTotal > 0 ? fmtCurrency(svc.lineTotal) : "";
          const unitAmountLabel = svc.unitAmount > 0 ? fmtCurrency(svc.unitAmount) : "";
          const computedDuration =
            durationFromTrip(item.startTripAt, item.completedTime) || item.duration || "";
          const photos = Array.isArray(item.proofPhotos) ? item.proofPhotos : [];
          const proofPhoto = photos[0]?.url ? `=HYPERLINK("${photos[0].url}", "Photo 1")` : "";
          const isFutile =
            Boolean(item.futile || jobData.futile) ||
            String(firstDefined(item.status, jobData.status, "")).toLowerCase() === "futile" ||
            String(firstDefined(item.skipReason, jobData.skipReason, "")).toLowerCase() === "futile";
          const futileAmount = isFutile
            ? toNumber(
                firstDefined(
                  item.futileCharge,
                  item.futileAmount,
                  jobData.futileCharge,
                  jobData.futileAmount,
                  FUTILE_CHARGE_AMOUNT
                )
              ) || FUTILE_CHARGE_AMOUNT
            : 0;
          const futileNote =
            firstDefined(item.futileNote, jobData.futileNote, item.skipNote, jobData.skipNote, "") || "";

          rows.push({
            Customer: customerName,
            Date: dateLabel,
            Address: addrToText(item.address) || "",
            Site: siteName,
            Run: runKey,
            Service: svc.service,
            QtyBinsCollected: svc.qty || "",
            UnitAmount: unitAmountLabel,
            LineTotal: lineTotalLabel,
            DriverNotes: driverNotes || description,
            Description: description,
            PONumber: firstDefined(item.poNumber, item.po, item.purchaseOrderNumber, jobData.poNumber, jobData.po, jobData.purchaseOrderNumber, jobData.sitePoNumber, jobData?.site?.poNumber, "") || "",
            ETA: item.eta || "",
            StartTrip: item.startTripAt || "",
            JobStart: item.startJobAt || item.arrivedAt || "",
            Finished: item.completedTime || "",
            Duration: computedDuration,
            UploadedBy: emailPrefix,
            ProofPhoto: proofPhoto,
            SiteContact: firstDefined(item.siteContact, jobData.siteContact, "") || "",
            PhoneNumber: firstDefined(item.sitePhone, jobData.sitePhone, "") || "",
            BreakDetails: breakDetails,
            PreStartStart: "",
            PreStartEnd: "",
            PreStartAt: "",
            Skipped: item.skipped ? "Yes" : "No",
            SkipReason: item.skipReason || "",
            SkipNote: item.skipNote || "",
            SkippedAt: item.skippedAt || "",
            SkippedBy: item.skippedByEmail || "",
            Futile: isFutile ? "Yes" : "No",
            FutileCharge: isFutile ? fmtCurrency(futileAmount) : "",
            FutileNote: isFutile ? futileNote : "",
            PostTripOdoEnd: postTripDoc?.odometerEnd != null ? String(postTripDoc.odometerEnd) : "",
            PostTripEndTime: postTripDoc?.tripEndTime || "",
            PostTripNotes: postTripDoc?.notes || "",
          });
        });
      });
    }

    const selectedHeaders = EXPORT_COLUMN_OPTIONS
      .filter(([key]) => selectedExportColumns.includes(key))
      .map(([key]) => key);

    let outputRows = [...rows];
    if (groupExportBySite) {
      outputRows.sort((a, b) =>
        [a.Customer, a.Site, a.Address, a.Date, a.Run]
          .join(" ")
          .localeCompare([b.Customer, b.Site, b.Address, b.Date, b.Run].join(" "))
      );

      const groupedRows = [];
      let currentGroup = "";
      outputRows.forEach((row) => {
        const groupKey = `${row.Customer}|||${row.Site || row.Address}`;
        if (groupKey !== currentGroup) {
          currentGroup = groupKey;
          const label = `${row.Customer}${row.Site ? ` - ${row.Site}` : row.Address ? ` - ${row.Address}` : ""}`;
          const groupRow = Object.fromEntries(selectedHeaders.map((key) => [key, ""]));
          const firstHeader = selectedHeaders[0];
          if (firstHeader) groupRow[firstHeader] = `Site group: ${label}`;
          groupedRows.push(groupRow);
        }
        groupedRows.push(row);
      });
      outputRows = groupedRows;
    }

    const csvContent = [
      selectedHeaders,
      ...outputRows.map((row) => selectedHeaders.map((header) => csvEscape(row[header] ?? ""))),
    ]
      .map((line) => line.join(","))
      .join("\n");

    const blob = new Blob(["\ufeff", csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `${companySlug}_${(driverEmail.split("@")[0] || "driver").replace(/[^\w.-]/g, "_")}_${exportFromDate}_to_${exportToDate}_advanced.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = async () => {
    if (!assignment || !assignment.runs) return;

    const jobNotes = { ...jobNotesById };
    const idsToCheck = [];
    Object.values(assignment.runs).forEach((addresses) => {
      (addresses || []).forEach((item) => {
        if (item?.isWeighbridgeDocket) return;
        if (item?.jobId && jobNotes[item.jobId] === undefined) {
          idsToCheck.push(item.jobId);
        }
      });
    });

    if (idsToCheck.length) {
      await Promise.all(
        idsToCheck.map(async (id) => {
          try {
            const s = await getDoc(doc(db, "jobs", id));
            jobNotes[id] = s.exists() ? s.data().driverNotes ?? "" : "";
          } catch {
            jobNotes[id] = "";
          }
        })
      );
      setJobNotesById((prev) => ({ ...prev, ...jobNotes }));
    }

    const emailPrefix = (driverEmail.split("@")[0] || "driver").replace(
      /[^\w.-]/g,
      "_"
    );
    const rows = [];

    const breaksArr = Array.isArray(assignment.breaks)
      ? assignment.breaks
      : [];

    const breakDetails = breaksArr
      .map((b, idx) => {
        if (!b) return "";
        const s = b.start || "";
        const e = b.end || "";
        if (!s && !e) return "";
        return `#${idx + 1} ${s || "?"} - ${e || "?"}`;
      })
      .filter(Boolean)
      .join(" | ");

    const postOdoEnd =
      postTrip && postTrip.odometerEnd != null
        ? String(postTrip.odometerEnd)
        : "";
    const postTripEndTime = postTrip?.tripEndTime || "";
    const postTripNotes = postTrip?.notes || "";

    Object.entries(assignment.runs).forEach(([runKey, addresses]) => {
      const runSummary = runSummaries.find((r) => r.runKey === runKey) || null;
      const runPrestart =
        (runSummary && prestartsById?.[runSummary.prestartDocId]) ||
        primaryPrestart ||
        null;
      const preStartStart = pickPreStartStart(runPrestart) || "";
      const preStartEnd = pickPreStartEnd(runPrestart) || "";

      (addresses || []).forEach((item) => {
        if (!item || item.isWeighbridgeDocket) return;

        const computedDuration =
          durationFromTrip(item.startTripAt, item.completedTime) ||
          item.duration ||
          "";

        const baseData = {
          Address: addrToText(item.address) || "",
          ETA: item.eta || "",
          StartTrip: item.startTripAt || "",
          JobStart: item.startJobAt || item.arrivedAt || "",
          Finished: item.completedTime || "",
          Duration: computedDuration,
          UploadedBy: emailPrefix,
          ProofPhoto: "",
          Description: item.description || "",
          PONumber: item.poNumber || "",
          SiteContact: item.siteContact || "",
          PhoneNumber: item.sitePhone || "",
          BreakDetails: breakDetails,
          PreStartStart: preStartStart,
          PreStartEnd: preStartEnd,
          PreStartAt: preStartEnd,
          Skipped: item.skipped ? "Yes" : "No",
          SkipReason: item.skipReason || "",
          SkipNote: item.skipNote || "",
          SkippedAt: item.skippedAt || "",
          SkippedBy: item.skippedByEmail || "",
          DriverNotes: item.jobId ? jobNotes[item.jobId] || "" : "",
          PostTripOdoEnd: postOdoEnd,
          PostTripEndTime: postTripEndTime,
          PostTripNotes: postTripNotes,
        };

        const photos = Array.isArray(item.proofPhotos) ? item.proofPhotos : [];
        if (photos.length > 0) {
          photos.forEach((photo, i) => {
            const url = photo?.url || "";
            rows.push({
              ...baseData,
              ProofPhoto: url ? `=HYPERLINK("${url}", "Photo ${i + 1}")` : "",
            });
          });
        } else {
          rows.push(baseData);
        }
      });
    });

    const headers = [
      "Address",
      "ETA",
      "StartTrip",
      "JobStart",
      "Finished",
      "Duration",
      "UploadedBy",
      "ProofPhoto",
      "Description",
      "PONumber",
      "SiteContact",
      "PhoneNumber",
      "BreakDetails",
      "PreStartStart",
      "PreStartEnd",
      "PreStartAt",
      "Skipped",
      "SkipReason",
      "SkipNote",
      "SkippedAt",
      "SkippedBy",
      "DriverNotes",
      "PostTripOdoEnd",
      "PostTripEndTime",
      "PostTripNotes",
    ];

    const csvContent = [
      headers,
      ...rows.map((r) =>
        headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
      ),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const safeDate = date.replace(/[^\d-]/g, "_");
    link.setAttribute(
      "download",
      `${companySlug}_${emailPrefix}_${safeDate}_jobs.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllPhotos = async () => {
    if (!assignment || !assignment.runs) return;

    const allPhotos = [];
    Object.entries(assignment.runs || {}).forEach(([runKey, addresses]) => {
      (addresses || []).forEach((item, index) => {
        if (!item) return;
        const photos = Array.isArray(item.proofPhotos) ? item.proofPhotos : [];
        photos.forEach((p, photoIndex) => {
          const extFromName = p?.name?.includes(".")
            ? p.name.split(".").pop()
            : "";
          const ext =
            extFromName ||
            (p?.contentType === "image/png"
              ? "png"
              : p?.contentType === "image/webp"
                ? "webp"
                : "jpg");

          const stopPart = item?.isWeighbridgeDocket
            ? "docket"
            : `stop_${String(index + 1).padStart(2, "0")}`;

          const baseName =
            p?.name ||
            `${runKey}_${stopPart}_${String(photoIndex + 1).padStart(2, "0")}.${ext}`;

          allPhotos.push({
            ...p,
            _suggestedName: `${runKey}_${stopPart}_${baseName}`,
          });
        });
      });
    });

    if (allPhotos.length === 0) {
      alert("No photos to download.");
      return;
    }

    setZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder("photos");

      const CONCURRENCY = 4;
      let cursor = 0;
      const worker = async () => {
        while (cursor < allPhotos.length) {
          const i = cursor++;
          const photo = allPhotos[i];
          const safeName = (
            photo._suggestedName ||
            photo.name ||
            `photo_${i}.jpg`
          ).replace(/[^\w.\- ]+/g, "_");

          try {
            const blob = await getPhotoBlob(photo);
            folder.file(safeName, blob);
          } catch (e) {
            console.error("Photo fetch failed:", photo, e);
          }
        }
      };

      await Promise.all(new Array(CONCURRENCY).fill(0).map(worker));

      const emailPrefix = (driverEmail.split("@")[0] || "driver").replace(
        /[^\w.-]/g,
        "_"
      );
      const safeDate = date.replace(/[^\d-]/g, "_");
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${companySlug}_${emailPrefix}_${safeDate}_photos.zip`);
    } catch (err) {
      console.error("Zip error:", err);
      alert("Failed to prepare ZIP. See console for details.");
    } finally {
      setZipping(false);
    }
  };

  const TRI_LABEL = { ok: "No Defect", fault: "Fault Identified", na: "N/A" };

  const CHECK_1 = [
    ["engineOil", PRESTART_LABELS.engineOil],
    ["tyres", PRESTART_LABELS.tyres],
    ["wheelSecurity", PRESTART_LABELS.wheelSecurity],
    ["coolant", PRESTART_LABELS.coolant],
    ["seatbelt", PRESTART_LABELS.seatbelt],
    ["airLines", PRESTART_LABELS.airLines],
    ["lights", PRESTART_LABELS.lights],
    ["beacons", PRESTART_LABELS.beacons],
    ["mirrorsWindows", PRESTART_LABELS.mirrorsWindows],
    ["leaks", PRESTART_LABELS.leaks],
  ];
  const CHECK_2 = [
    ["lifterCompactor", PRESTART_LABELS.lifterCompactor],
    ["damagePanels", PRESTART_LABELS.damagePanels],
    ["kits", PRESTART_LABELS.kits],
    ["extinguisher", PRESTART_LABELS.extinguisher],
    ["eStops", PRESTART_LABELS.eStops],
    ["scales", PRESTART_LABELS.scales],
    ["numberPlate", PRESTART_LABELS.numberPlate],
    ["placards", PRESTART_LABELS.placards],
    ["wipers", PRESTART_LABELS.wipers],
    ["horn", PRESTART_LABELS.horn],
  ];
  const CHECK_3 = [
    ["radio", PRESTART_LABELS.radio],
    ["cameras", PRESTART_LABELS.cameras],
    ["brakeGauges", PRESTART_LABELS.brakeGauges],
    ["smartBrake", PRESTART_LABELS.smartBrake],
    ["parkBrake", PRESTART_LABELS.parkBrake],
    ["footBrake", PRESTART_LABELS.footBrake],
  ];

  const appendPrestartPages = (pdf, availablePrestarts, addPageBefore = false) => {
    const mm = (n) => n * 2.8346;
    const margin = mm(15);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;

    const colLabel = contentWidth * 0.55;
    const colStatus = 110;
    const colComment = contentWidth - colLabel - colStatus;

    availablePrestarts.forEach((entry, index) => {
      const p = entry.data || {};
      const ck = p.checklist || {};
      const cm = p.comments || {};
      const dec = p.declaration || {};

      if (addPageBefore || index > 0) pdf.addPage();

      const driverLabel = p.email || driverEmail || selectedDriverId;
      pdf.setFontSize(18);
      pdf.text("Daily Pre-Start Check", margin, mm(18));
      pdf.setFontSize(11);
      pdf.text(`Driver: ${driverLabel}`, margin, mm(26));
      pdf.text(`Run: ${entry.runKey || p.runKey || "-"}`, margin, mm(32));
      pdf.text(
        `Vehicle: ${
          p.vehicleLabel || p.registrationNo || entry.vehicleLabel || "-"
        }`,
        margin,
        mm(38)
      );

      const started = pickPreStartStart(p) || "-";
      const completed = pickPreStartEnd(p) || "-";
      pdf.text(`Started: ${started}`, margin + contentWidth * 0.55, mm(32));
      pdf.text(`Completed: ${completed}`, margin + contentWidth * 0.55, mm(38));

      autoTable(pdf, {
        startY: mm(46),
        margin: { left: margin, right: margin },
        theme: "grid",
        styles: { fontSize: 11, cellPadding: 6, overflow: "linebreak" },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
          lineColor: [59, 130, 246],
          lineWidth: 0.5,
        },
        head: [["Field", "Value"]],
        body: [
          ["Run", p.runKey || entry.runKey || "-"],
          ["Company", p.companyName || entry.companyName || "-"],
          [
            "Vehicle",
            p.vehicleLabel || p.registrationNo || entry.vehicleLabel || "-",
          ],
          [
            "Unit Type",
            p.unitType || p.vehicleLoadType || entry.vehicleLoadType || "-",
          ],
          [
            "Registration No.",
            p.registrationNo || p.vehicleRego || entry.vehicleRego || "-",
          ],
          ["Email Address", p.email || "-"],
          ["Odometer Start", p.odometerStart ?? "-"],
          ["Hour Meter Start", p.hourMeterStart ?? "-"],
          ["Shift Start Time", p.shiftStartTime || "-"],
          ["Fit for duty", dec.fitForDuty === true ? "Yes" : "No"],
          [
            "License/Training/PPE valid",
            dec.licenseTrainingPPE === true ? "Yes" : "No",
          ],
          ["Inspection done", dec.inspectionDone === true ? "Yes" : "No"],
        ],
        columnStyles: {
          0: { cellWidth: contentWidth * 0.28 },
          1: { cellWidth: contentWidth * 0.72 },
        },
      });

      const tableSection = (title, rows) =>
        autoTable(pdf, {
          startY: pdf.lastAutoTable.finalY + mm(6),
          margin: { left: margin, right: margin },
          theme: "grid",
          styles: {
            fontSize: 11,
            cellPadding: 6,
            overflow: "linebreak",
            valign: "middle",
          },
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "left",
            lineColor: [59, 130, 246],
            lineWidth: 0.5,
          },
          bodyStyles: { valign: "top", textColor: [45, 45, 45] },
          alternateRowStyles: { fillColor: [247, 250, 252] },
          head: [[title, "Status", "Comment"]],
          body: rows.map(([k, label]) => [
            label,
            TRI_LABEL[ck[k]] || "",
            ck[k] === "fault" ? cm[k] || "" : "",
          ]),
          columnStyles: {
            0: { cellWidth: colLabel },
            1: { cellWidth: colStatus, halign: "center", fontStyle: "bold" },
            2: { cellWidth: colComment },
          },
          didParseCell: (data) => {
            if (data.section === "body" && data.column.index === 1) {
              const key = rows[data.row.index][0];
              if (ck[key] === "fault") data.cell.styles.textColor = [200, 0, 0];
            }
          },
        });

      tableSection("Pre-Start Checklist — Group 1", CHECK_1);
      tableSection("Pre-Start Checklist — Group 2", CHECK_2);
      tableSection("Pre-Start Checklist — Continue", CHECK_3);

      if (p.signature) {
        const y = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + mm(10) : mm(46);
        pdf.setFontSize(11);
        pdf.text("Driver Signature:", margin, y);
        try {
          pdf.addImage(p.signature, "PNG", margin, y + mm(2), mm(80), mm(25));
        } catch {
          //
        }
      }
    });

    return availablePrestarts.length;
  };

  const appendPostTripPages = (pdf, entries, addPageBefore = false) => {
    const margin = 40;
    const driverLabel = driverEmail || selectedDriverId;

    entries.forEach(({ run, data }, index) => {
      if (addPageBefore || index > 0) pdf.addPage();

      const pre =
        prestartsById?.[run.prestartDocId] ||
        prestartsById?.[data.prestartDocId] ||
        null;
      const truck =
        data.vehicleLabel ||
        data.registrationNo ||
        data.vehicleRego ||
        pre?.vehicleLabel ||
        pre?.registrationNo ||
        run.vehicleLabel ||
        "-";
      const company =
        data.companyName ||
        pre?.companyName ||
        run.companyName ||
        data.companyId ||
        pre?.companyId ||
        "-";

      const odoStart = Number(pre?.odometerStart);
      const odoEnd = Number(data.odometerEnd);
      const hourStart = Number(pre?.hourMeterStart);
      const hourEnd = Number(data.hourMeterEnd);

      const odoUsed =
        Number.isFinite(odoStart) && Number.isFinite(odoEnd)
          ? odoEnd - odoStart
          : "-";
      const hourUsed =
        Number.isFinite(hourStart) && Number.isFinite(hourEnd)
          ? hourEnd - hourStart
          : "-";
      const runLabel = data.runKey || run.runKey || "-";
      const started = pre ? pickPreStartStart(pre) || "-" : "-";
      const preCompleted = pre ? pickPreStartEnd(pre) || "-" : "-";

      pdf.setFontSize(18);
      pdf.text("Post Trip Report", margin, 40);
      pdf.setFontSize(11);
      pdf.text(`Driver: ${driverLabel}`, margin, 60);
      pdf.text(`Run: ${runLabel}`, margin, 75);
      pdf.text(`Vehicle: ${truck}`, margin, 90);
      pdf.text(`Date: ${date}`, margin, 105);
      pdf.text(`Pre-start started: ${started}`, margin + 380, 75);
      pdf.text(`Pre-start completed: ${preCompleted}`, margin + 380, 90);
      pdf.text(`Post-trip submitted: ${data.completedAt || "-"}`, margin + 380, 105);

      autoTable(pdf, {
        startY: 125,
        margin: { left: margin, right: margin },
        theme: "grid",
        styles: { fontSize: 11, cellPadding: 6, overflow: "linebreak" },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
          lineColor: [59, 130, 246],
          lineWidth: 0.5,
        },
        head: [["Field", "Value"]],
        body: [
          ["Run", runLabel],
          ["Company", company],
          ["Vehicle", truck],
          ["Unit Type", data.unitType || data.vehicleLoadType || pre?.unitType || pre?.vehicleLoadType || run.vehicleLoadType || "-"],
          ["Registration No.", data.registrationNo || data.vehicleRego || pre?.registrationNo || pre?.vehicleRego || run.vehicleRego || "-"],
          ["Email Address", data.driverEmail || pre?.email || driverLabel || "-"],
          ["Odometer Start", pre?.odometerStart ?? "-"],
          ["Odometer End", data.odometerEnd ?? "-"],
          ["Odometer Used", odoUsed],
          ["Hour Meter Start", pre?.hourMeterStart ?? "-"],
          ["Hour Meter End", data.hourMeterEnd ?? "-"],
          ["Hour Meter Used", hourUsed],
          ["Trip End Time", data.tripEndTime || "-"],
          ["Post-Trip Submitted", data.completedAt || "-"],
          ["Notes", data.notes || "-"],
        ],
        columnStyles: {
          0: { cellWidth: 200 },
          1: { cellWidth: pdf.internal.pageSize.getWidth() - margin * 2 - 200 },
        },
      });

      if (!data.signature) return;
      const y = (pdf.lastAutoTable?.finalY || 95) + 25;
      pdf.setFontSize(11);
      pdf.text("Driver Signature:", margin, y);
      try {
        pdf.addImage(data.signature, "PNG", margin, y + 5, 200, 60);
      } catch {
        //
      }
    });

    return entries.length;
  };

  const exportTruckChecksPDF = async () => {
    if (!selectedDriverId || !date) {
      alert("Pick driver and date first.");
      return;
    }

    const pick = (...values) =>
      values.find(
        (value) =>
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
      ) || "";
    const normalizeVehicleKey = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const safeFilePart = (value, fallback = "vehicle") =>
      String(value || fallback)
        .trim()
        .replace(/[^a-zA-Z0-9.-]+/g, "_")
        .replace(/^_+|_+$/g, "") || fallback;

    const groups = new Map();
    const addRecord = (run = {}, pre = null, post = null) => {
      if (!pre && !post) return;

      const vehicleRego = pick(
        post?.registrationNo,
        post?.vehicleRego,
        pre?.registrationNo,
        pre?.vehicleRego,
        run.vehicleRego
      );
      const vehicleId = pick(post?.vehicleId, pre?.vehicleId, run.vehicleId);
      const vehicleLabel = pick(
        post?.vehicleLabel,
        pre?.vehicleLabel,
        run.vehicleLabel,
        vehicleRego,
        "Vehicle not recorded"
      );
      const vehicleKey =
        normalizeVehicleKey(vehicleRego) ||
        normalizeVehicleKey(vehicleId) ||
        normalizeVehicleKey(vehicleLabel) ||
        "vehicle-not-recorded";

      if (!groups.has(vehicleKey)) {
        groups.set(vehicleKey, {
          vehicleRego,
          vehicleLabel,
          prestarts: new Map(),
          postTrips: new Map(),
        });
      }

      const group = groups.get(vehicleKey);
      if (!group.vehicleRego && vehicleRego) group.vehicleRego = vehicleRego;
      if (group.vehicleLabel === "Vehicle not recorded" && vehicleLabel) {
        group.vehicleLabel = vehicleLabel;
      }

      if (pre) {
        const preKey =
          run.prestartDocId ||
          pre.prestartDocId ||
          pre.runKey ||
          run.runKey ||
          `prestart-${group.prestarts.size}`;
        if (!group.prestarts.has(preKey)) {
          group.prestarts.set(preKey, { ...run, data: pre });
        }
      }

      if (post) {
        const postKey =
          run.postTripDocId ||
          post.postTripDocId ||
          post.runKey ||
          run.runKey ||
          `posttrip-${group.postTrips.size}`;
        if (!group.postTrips.has(postKey)) {
          group.postTrips.set(postKey, { run, data: post });
        }
      }
    };

    runSummaries.forEach((run) => {
      const pre = prestartsById?.[run.prestartDocId] || null;
      const post =
        postTripsById?.[run.postTripDocId] ||
        postTripsById?.[run.prestartDocId] ||
        null;
      addRecord(run, pre, post);
    });

    if (postTrip && !Array.from(groups.values()).some((group) => group.postTrips.size)) {
      const matchingKey =
        normalizeVehicleKey(
          postTrip.registrationNo || postTrip.vehicleRego || postTrip.vehicleLabel
        ) || (groups.size === 1 ? groups.keys().next().value : "");
      const matchingGroup = matchingKey ? groups.get(matchingKey) : null;
      if (matchingGroup) {
        matchingGroup.postTrips.set("legacy-day-posttrip", {
          run: {
            runKey: postTrip.runKey || "Day record",
            vehicleLabel: matchingGroup.vehicleLabel,
            vehicleRego: matchingGroup.vehicleRego,
          },
          data: postTrip,
        });
      } else {
        addRecord(
          {
            runKey: postTrip.runKey || "Day record",
            vehicleLabel: postTrip.vehicleLabel || "",
            vehicleRego: postTrip.registrationNo || postTrip.vehicleRego || "",
          },
          null,
          postTrip
        );
      }
    }

    if (!groups.size) {
      alert("No pre-start or post-trip records found for this driver/date.");
      return;
    }

    const safeDate = date.replace(/[^\d-]/g, "_");
    const emailPrefix = safeFilePart(
      (driverEmail || selectedDriverId || "driver").split("@")[0],
      "driver"
    );
    const generated = [];

    groups.forEach((group, vehicleKey) => {
      const preEntries = Array.from(group.prestarts.values());
      const postEntries = Array.from(group.postTrips.values());
      if (!preEntries.length && !postEntries.length) return;

      const pdf = new jsPDF({
        unit: "pt",
        format: "a4",
        orientation: "landscape",
      });
      const prePageCount = preEntries.length
        ? appendPrestartPages(pdf, preEntries)
        : 0;
      if (postEntries.length) {
        appendPostTripPages(pdf, postEntries, prePageCount > 0);
      }

      const vehiclePart = safeFilePart(
        group.vehicleRego || group.vehicleLabel || vehicleKey
      );
      generated.push({
        pdf,
        filename: `truck_checks_${vehiclePart}_${emailPrefix}_${safeDate}.pdf`,
      });
    });

    if (generated.length === 1) {
      generated[0].pdf.save(generated[0].filename);
      return;
    }

    const zip = new JSZip();
    generated.forEach(({ pdf, filename }) => {
      zip.file(filename, pdf.output("arraybuffer"));
    });
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `truck_checks_${emailPrefix}_${safeDate}.zip`);
  };

  const exportSummaryPDF = async () => {
    if (!assignment || !assignment.runs) {
      alert("No jobs loaded.");
      return;
    }

    const jobList = [];
    Object.entries(assignment.runs || {}).forEach(([runKey, addresses]) => {
      (addresses || []).forEach((item, idx) => {
        if (!item || item.isWeighbridgeDocket) return;
        jobList.push({ runKey, index: idx + 1, item });
      });
    });

    if (!jobList.length) {
      alert("No normal jobs to summarise.");
      return;
    }

    const jobsWithPhotos = await Promise.all(
      jobList.map(async (j) => {
        const photos = Array.isArray(j.item.proofPhotos)
          ? j.item.proofPhotos
          : [];
        const photoData = [];

        const maxPhotos = 6;
        for (let i = 0; i < photos.length && i < maxPhotos; i++) {
          try {
            const blob = await getPhotoBlob(photos[i]);
            const type =
              blob.type === "image/png"
                ? "PNG"
                : blob.type === "image/jpeg"
                  ? "JPEG"
                  : "PNG";

            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = (err) => reject(err);
              reader.readAsDataURL(blob);
            });

            photoData.push({ dataUrl, type });
          } catch (e) {
            console.error("Photo preview failed:", e);
          }
        }

        return { ...j, photos: photoData };
      })
    );

    const pdf = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;

    const drawHeader = () => {
      pdf.setFontSize(18);
      pdf.text("Job Summary", margin, 36);
      pdf.setFontSize(11);
      pdf.text(
        `Driver: ${driverEmail || selectedDriverId || "Unknown"}`,
        margin,
        54
      );
      pdf.text(`Date: ${date}`, margin, 68);
      if (primaryPrestart?.registrationNo || prestartList[0]?.vehicleLabel) {
        pdf.text(
          `Vehicle: ${
            primaryPrestart?.registrationNo || prestartList[0]?.vehicleLabel
          }${
            primaryPrestart?.odometerStart
              ? ` (Odo Start: ${primaryPrestart.odometerStart})`
              : ""
          }`,
          margin,
          82
        );
      }
    };

    drawHeader();

    let y = 100;

    const ensureSpace = (needed) => {
      if (y + needed <= pageHeight - margin) return;
      pdf.addPage();
      drawHeader();
      y = 100;
    };

    pdf.setFontSize(11);

    jobsWithPhotos.forEach((job, idx) => {
      const { runKey, index, item, photos } = job;

      const duration =
        durationFromTrip(item.startTripAt, item.completedTime) ||
        item.duration ||
        "";

      const driverNote =
        item.jobId && jobNotesById[item.jobId]
          ? jobNotesById[item.jobId]
          : "";
      const isFutile =
        !!item.futile ||
        String(item.status || "").toLowerCase() === "futile" ||
        String(item.skipReason || "").toLowerCase() === "futile";
      const futileAmount =
        Number(item.futileCharge || item.futileAmount || FUTILE_CHARGE_AMOUNT) ||
        FUTILE_CHARGE_AMOUNT;
      const futileNote = item.futileNote || item.skipNote || "";

      const lines = [];
      lines.push(`Run: ${runKey}   Stop: ${index}`);
      lines.push(`Address: ${addrToText(item.address) || ""}`);
      if (item.eta) lines.push(`ETA: ${item.eta}`);
      if (item.startTripAt) lines.push(`Start Trip: ${item.startTripAt}`);
      if (item.startJobAt || item.arrivedAt)
        lines.push(`Job Start: ${item.startJobAt || item.arrivedAt}`);
      if (item.completedTime) lines.push(`Finished: ${item.completedTime}`);
      if (duration) lines.push(`Duration: ${duration}`);
      if (item.description) lines.push(`Description: ${item.description}`);
      if (driverNote) lines.push(`Note: ${driverNote}`);
      if (isFutile) {
        lines.push(`FUTILE Charge: ${fmtCurrency(futileAmount)}`);
        if (futileNote) lines.push(`FUTILE Note: ${futileNote}`);
      }
      if (item.skipped) {
        lines.push(`Skipped: YES`);
        if (item.skipReason) lines.push(`Skip Reason: ${item.skipReason}`);
        if (item.skipNote) lines.push(`Skip Note: ${item.skipNote}`);
      }

      const textHeightPerLine = 14;
      const textBlockHeight = lines.length * textHeightPerLine + 10;

      ensureSpace(textBlockHeight + (photos.length ? 120 : 40));

      pdf.setFontSize(12);
      pdf.setFont(undefined, "bold");
      pdf.text(`Job ${idx + 1}`, margin, y);
      pdf.setFontSize(11);
      pdf.setFont(undefined, "normal");

      y += 16;

      const wrapped = lines.flatMap((line) =>
        pdf.splitTextToSize(line, pageWidth - margin * 2)
      );
      wrapped.forEach((l) => {
        pdf.text(l, margin, y);
        y += textHeightPerLine;
      });

      if (photos.length) {
        y += 4;
        const photoWidth = 140;
        const photoHeight = 100;
        const gapX = 10;
        const perRow = 3;

        photos.forEach((p, i) => {
          if (y + photoHeight > pageHeight - margin) {
            pdf.addPage();
            drawHeader();
            y = 100;
          }
          const col = i % perRow;
          const row = Math.floor(i / perRow);
          const x = margin + col * (photoWidth + gapX);
          const yPhoto = y + row * (photoHeight + 10);

          if (yPhoto + photoHeight > pageHeight - margin) {
            pdf.addPage();
            drawHeader();
            y = 100;
          }

          try {
            pdf.addImage(
              p.dataUrl,
              p.type || "PNG",
              x,
              yPhoto,
              photoWidth,
              photoHeight
            );
          } catch (e) {
            console.error("addImage failed:", e);
          }

          if ((i + 1) % perRow === 0 || i === photos.length - 1) {
            y = yPhoto + photoHeight + 14;
          }
        });
      } else {
        y += 8;
      }

      y += 6;
    });

    const emailPrefix = (driverEmail.split("@")[0] || "driver").replace(
      /[^\w.-]/g,
      "_"
    );
    const safeDate = date.replace(/[^\d-]/g, "_");
    pdf.save(`summary_${emailPrefix}_${safeDate}.pdf`);
  };

  const prestartSummaryRows = useMemo(
    () =>
      prestartList.map((entry) => ({
        ...entry,
        faults: getFaultsForPrestart(entry.data),
      })),
    [prestartList]
  );

  const formatBreakTime = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      const m = value.match(/^(\d{1,2}:\d{2})/);
      if (m) return m[1];
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-AU", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "";
    }
    if (typeof value === "number") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-AU", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }
    return "";
  };

  const breaksArr = useMemo(() => {
    if (!assignment) return [];
    if (Array.isArray(assignment.breaks) && assignment.breaks.length) {
      return assignment.breaks;
    }
    if (
      assignment.breakStartedAt ||
      assignment.breakEndedAt ||
      assignment.breakDuration
    ) {
      return [
        {
          start: assignment.breakStartedAt || null,
          end: assignment.breakEndedAt || null,
          duration: assignment.breakDuration || null,
        },
      ];
    }
    return [];
  }, [assignment]);

  const visibleBreaks = (breaksArr || []).filter((b) => {
    const s = formatBreakTime(b.start);
    const e = formatBreakTime(b.end);
    return s || e;
  });

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 text-gray-900">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="relative min-w-[260px] flex-1">
          <input
            type="search"
            value={driverSearch}
            onChange={(e) => setDriverSearch(e.target.value)}
            onFocus={() => setDriverPickerOpen(true)}
            onBlur={() => setTimeout(() => setDriverPickerOpen(false), 120)}
            placeholder="Search by rego, driver, run, or company..."
            className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
          />
          {driverPickerOpen && (
            <div className="absolute left-0 right-0 top-[46px] z-50 max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
              {historySearchOptions.length ? (
                historySearchOptions.map((option) => {
                  const isSelected =
                    option.driverId === selectedDriverId &&
                    (!option.runKey || option.runKey === selectedRunKey);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectHistorySearchOption(option)}
                      className={`mb-1 flex w-full flex-col items-start rounded-lg px-3 py-2 text-left text-sm transition ${
                        isSelected
                          ? "bg-emerald-50 text-emerald-700"
                          : "text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      <span className="font-medium">{option.primary}</span>
                      {option.secondary ? (
                        <span className="mt-0.5 text-xs text-gray-500">{option.secondary}</span>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No trucks or drivers match "{driverSearch.trim()}"
                </div>
              )}
            </div>
          )}
        </div>

        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
        />

        <div className="ml-auto text-xs text-gray-500">
          {(selectedTruckLabel || driverEmail) && (
            <>
              <span className="font-semibold text-gray-900">
                {selectedTruckLabel || driverEmail}
              </span>
              {selectedTruckLabel && driverEmail ? ` · ${driverEmail}` : ""}
              {" · "}
            </>
          )}
          <span
            className={
              loading
                ? "text-gray-500"
                : assignment
                  ? "font-semibold text-emerald-700"
                  : "text-gray-500"
            }
          >
            {loading ? "Loading..." : assignment ? "Loaded" : "Ready"}
          </span>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 mt-2">Loading assignment...</p>
      ) : assignment ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-sky-200 bg-sky-50 overflow-hidden">
            <button
              type="button"
              onClick={() => setPrestartsSectionOpen((open) => !open)}
              aria-expanded={prestartsSectionOpen}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">Pre-starts</span>
                <span className="inline-flex items-center rounded-full border border-sky-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-sky-800">
                  {prestartSummaryRows.filter((e) => e.startedAtLabel || e.completedAtLabel).length}/{prestartSummaryRows.length} recorded
                </span>
              </span>
              <svg
                className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${prestartsSectionOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {prestartsSectionOpen && (
            <div className="space-y-4 px-4 pb-4">
            <p className="-mt-1 text-xs sm:text-sm text-gray-600">
                Shows the pre-start recorded for each run/vehicle for the selected day.
              </p>

            {prestartSummaryRows.length ? (
              <div className="space-y-3">
                {prestartSummaryRows.map((entry) => {
                  const p = entry.data;
                  return (
                    <div
                      key={entry.prestartDocId}
                      className="rounded-xl border border-sky-100 bg-white/80 p-3 sm:p-4"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {entry.runKey}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {entry.vehicleLabel ||
                              p?.vehicleLabel ||
                              p?.registrationNo ||
                              entry.companyName ||
                              "No vehicle recorded"}
                          </p>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-700">
                          {entry.startedAtLabel || entry.completedAtLabel ? (
                            <>
                              {entry.startedAtLabel && (
                                <span>Started at {entry.startedAtLabel}</span>
                              )}
                              {entry.startedAtLabel && entry.completedAtLabel && (
                                <span> – </span>
                              )}
                              {entry.completedAtLabel && (
                                <span>Completed at {entry.completedAtLabel}</span>
                              )}
                            </>
                          ) : (
                            <span>Not completed</span>
                          )}
                        </div>
                      </div>

                      {(p?.registrationNo || p?.odometerStart != null) && (
                        <p className="mt-2 text-xs sm:text-sm text-gray-700">
                          Vehicle: {p?.registrationNo || entry.vehicleRego || "-"}
                          {p?.odometerStart != null && p?.odometerStart !== ""
                            ? ` (Odometer start: ${p.odometerStart})`
                            : ""}
                          {p?.hourMeterStart != null && p?.hourMeterStart !== ""
                            ? ` (Hour meter start: ${p.hourMeterStart})`
                            : ""}
                        </p>
                      )}

                      {entry.faults.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-red-700">
                            Faults reported:
                          </p>
                          <ul className="mt-1 list-disc ml-6 text-xs sm:text-sm text-gray-800 space-y-1">
                            {entry.faults.map((f) => (
                              <li key={`${entry.prestartDocId}_${f.key}`}>
                                <span className="font-medium">{f.label}:</span>{" "}
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: linkify(f.comment || "(no comment)"),
                                  }}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                No pre-starts found for this driver and date.
              </p>
            )}

            {visibleBreaks.length > 0 && (
              <p className="mt-1 text-xs sm:text-sm text-gray-700">
                Breaks:&nbsp;
                {visibleBreaks.map((b, idx) => {
                  const s = formatBreakTime(b.start) || "";
                  const e = b.end ? formatBreakTime(b.end) : "";
                  return (
                    <span key={idx}>
                      {idx > 0 && " | "}
                      {s}
                      {s || e ? " - " : ""}
                      {e || (b.end == null && "…")}
                    </span>
                  );
                })}
              </p>
            )}
            </div>
            )}
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 overflow-hidden">
            <button
              type="button"
              onClick={() => setPostTripSectionOpen((open) => !open)}
              aria-expanded={postTripSectionOpen}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">Post trip</span>
                <span className="inline-flex items-center rounded-full border border-emerald-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                  {runSummaries.filter((run) => postTripsById?.[run.postTripDocId] || postTripsById?.[run.prestartDocId]).length}/{runSummaries.length} recorded
                </span>
              </span>
              <svg
                className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${postTripSectionOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {postTripSectionOpen && (
            <div className="space-y-4 px-4 pb-4">

          <div className="rounded-xl border border-emerald-200 bg-white/70 p-3 space-y-1">
            <p className="flex flex-wrap gap-x-2 text-sm text-gray-800">
              <span className="font-semibold">Post trip:</span>
              {postTripSummary ? (
                <>
                  {postTripSummary.tripEndTime && (
                    <span>Trip end at {postTripSummary.tripEndTime}</span>
                  )}
                  {postTripSummary.tripEndTime &&
                    postTripSummary.odometerEnd != null && <span>–</span>}
                  {postTripSummary.odometerEnd != null && (
                    <span>Odometer end {postTripSummary.odometerEnd}</span>
                  )}
                  {postTripSummary.hourMeterEnd != null && (
                    <span>Hour meter end {postTripSummary.hourMeterEnd}</span>
                  )}
                  {!postTripSummary.tripEndTime && postTripSummary.odometerEnd == null && (
                    <span>Recorded, but missing key fields</span>
                  )}
                </>
              ) : (
                <span>Not completed</span>
              )}
            </p>
            {postTripSummary?.notes && (
              <p className="mt-1 text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                <span className="font-semibold">Notes:</span> {postTripSummary.notes}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-emerald-200 bg-white/70 p-3 space-y-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Post trips by run</p>
              <p className="text-xs sm:text-sm text-gray-600">
                Shows one post-trip declaration for each run/truck where available.
              </p>
            </div>

            {runSummaries.length ? (
              <div className="space-y-3">
                {runSummaries.map((run) => {
                  const p =
                    postTripsById?.[run.postTripDocId] ||
                    postTripsById?.[run.prestartDocId] ||
                    null;
                  return (
                    <div
                      key={`post_${run.runKey}`}
                      className="rounded-xl border border-emerald-100 bg-white/80 p-3 sm:p-4"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {run.runKey}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {p?.vehicleLabel ||
                              p?.registrationNo ||
                              run.vehicleLabel ||
                              run.companyName ||
                              "No vehicle recorded"}
                          </p>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-700">
                          {p ? (
                            p.tripEndTime ? (
                              <span>Trip end at {p.tripEndTime}</span>
                            ) : (
                              <span>Recorded</span>
                            )
                          ) : (
                            <span>Not completed</span>
                          )}
                        </div>
                      </div>

                      {p ? (
                        <p className="mt-2 text-xs sm:text-sm text-gray-700">
                          {p.odometerEnd != null && p.odometerEnd !== ""
                            ? `Odometer end: ${p.odometerEnd}`
                            : "Odometer end: -"}
                          {" • "}
                          {p.hourMeterEnd != null && p.hourMeterEnd !== ""
                            ? `Hour meter end: ${p.hourMeterEnd}`
                            : "Hour meter end: -"}
                        </p>
                      ) : null}

                      {p?.notes && (
                        <p className="mt-2 text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                          <span className="font-semibold">Notes:</span> {p.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                No run-level post trips found for this driver and date.
              </p>
            )}
          </div>
            </div>
            )}
          </div>

          {Object.entries(assignment.runs || {}).map(([runKey, addresses]) => {
            const list = Array.isArray(addresses) ? addresses : [];
            const visibleStops = list.filter(
              (item) => item && !item.isWeighbridgeDocket
            );
            const docketRow = list.find((item) => item?.isWeighbridgeDocket);
            const docketDoc = weighbridgeDockets[runKey];

            return (
              <div
                key={runKey}
                ref={(node) => {
                  if (node) runCardRefs.current[runKey] = node;
                  else delete runCardRefs.current[runKey];
                }}
                tabIndex={selectedRunKey === runKey ? -1 : undefined}
                className={`bg-white rounded-xl shadow-sm p-4 space-y-4 outline-none transition ${
                  selectedRunKey === runKey
                    ? "border-2 border-emerald-500 ring-4 ring-emerald-100"
                    : "border border-gray-200"
                }`}
              >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {runKey}
                  </h3>
                  {(() => {
                    const runSummary = runSummaries.find((r) => r.runKey === runKey);
                    if (!runSummary) return null;
                    return (
                      <div className="text-xs sm:text-sm text-gray-600">
                        {runSummary.vehicleLabel || runSummary.companyName || ""}
                      </div>
                    );
                  })()}
                </div>

                {visibleStops.map((item, idx) => {
                  const durationDisplay =
                    durationFromTrip(item.startTripAt, item.completedTime) ||
                    item.duration ||
                    "";

                  const driverNote =
                    item.jobId && jobNotesById[item.jobId]
                      ? jobNotesById[item.jobId]
                      : "";
                  const isFutile =
                    !!item.futile ||
                    String(item.status || "").toLowerCase() === "futile" ||
                    String(item.skipReason || "").toLowerCase() === "futile";
                  const futileAmount =
                    Number(item.futileCharge || item.futileAmount || FUTILE_CHARGE_AMOUNT) ||
                    FUTILE_CHARGE_AMOUNT;
                  const futileNote = item.futileNote || item.skipNote || "";

                  return (
                    <div
                      key={idx}
                      className="space-y-3 border-t border-gray-100 pt-4"
                    >
                      <div className="rounded-2xl border border-gray-200 bg-white/90 p-3 sm:p-4 shadow-sm">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium uppercase tracking-wide text-gray-400">
                              Stop {idx + 1}
                            </p>
                            <p className="mt-1 text-base font-semibold leading-6 text-gray-900 break-words">
                              {addrToText(item.address) || "(no address)"}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2 lg:max-w-[360px] lg:justify-end">
                            {item.jobId && (
                              <Link
                                to={buildJobHref(location, item.jobId, {
                                  runKey,
                                  date: date || "",
                                  driverId: selectedDriverId || "",
                                })}
                                state={{
                                  from: `${location.pathname}${location.search}`,
                                  runKey,
                                  date,
                                  driverId: selectedDriverId,
                                  stop: item,
                                }}
                                className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-black sm:text-sm"
                                title="Open job details"
                              >
                                Open job
                              </Link>
                            )}

                            {item.jobId && (
                              <button
                                onClick={() =>
                                  downloadConfirmationPdfForJob(item.jobId)
                                }
                                disabled={confirmDownloadingJobId === item.jobId}
                                className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-xs font-medium text-white transition sm:text-sm ${
                                  confirmDownloadingJobId === item.jobId
                                    ? "cursor-not-allowed bg-gray-400"
                                    : "bg-violet-600 hover:bg-violet-700"
                                }`}
                                title="Download service confirmation PDF"
                              >
                                {confirmDownloadingJobId === item.jobId
                                  ? "Downloading..."
                                  : "Download confirmation"}
                              </button>
                            )}

                            {item.jobId && hasJobDocket(item.jobId) && (
                              <button
                                onClick={() => viewJobDocket(item.jobId, false)}
                                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-blue-700 sm:text-sm"
                                title="View uploaded delivery docket"
                              >
                                View docket
                              </button>
                            )}

                            {item.jobId && hasSignedJobDocket(item.jobId) && (
                              <button
                                onClick={() => viewJobDocket(item.jobId, true)}
                                className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-emerald-700 sm:text-sm"
                                title="View signed delivery docket"
                              >
                                View signed docket
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm text-gray-700">
                        {item.eta && <div>ETA: {item.eta}</div>}
                        {item.startTripAt && <div>Start trip: {item.startTripAt}</div>}
                        {item.startJobAt && <div>Job start: {item.startJobAt}</div>}
                        {item.completedTime && <div>Finished: {item.completedTime}</div>}
                        {durationDisplay && <div>Duration: {durationDisplay}</div>}
                        {isFutile && <div>FUTILE charge: {fmtCurrency(futileAmount)}</div>}
                      </div>

                      {isFutile && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs sm:text-sm text-red-900">
                          <span className="font-semibold">FUTILE:</span>{" "}
                          {futileNote || "No note recorded."}
                        </div>
                      )}

                      {(item.description || driverNote) && (
                        <div className="text-xs sm:text-sm text-gray-700 space-y-1">
                          {item.description && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: `<b>Description:</b> ${linkify(
                                  item.description
                                )}`,
                              }}
                            />
                          )}
                          {driverNote && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: `<b>Driver note:</b> ${linkify(driverNote)}`,
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {docketRow && (
                  <div className="mt-4 rounded-lg border border-gray-200 p-4 bg-slate-50">
                    <h4 className="font-semibold text-gray-900 text-base sm:text-lg">
                      Weighbridge docket — {runKey}
                    </h4>
                    <p className="mt-1 text-xs sm:text-sm text-gray-600">
                      This docket belongs to the run only (not treated as a job card).
                    </p>

                    {Array.isArray(docketRow.proofPhotos) &&
                    docketRow.proofPhotos.length > 0 ? (
                      <div className="mt-3 space-y-1">
                        <p className="text-sm font-medium text-gray-700">
                          Photos
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {docketRow.proofPhotos.map((p, i) => (
                            <button
                              key={i}
                              onClick={() => downloadSinglePhoto(p)}
                              className="px-3 py-1.5 rounded-lg text-xs bg-gray-900 text-white hover:bg-black transition"
                            >
                              Download photo {i + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-gray-500">
                        No docket photos uploaded.
                      </p>
                    )}

                    {docketDoc?.comment ? (
                      <div className="mt-3 text-xs sm:text-sm text-gray-800">
                        <span className="font-medium">
                          Driver comment (weighbridge docket):
                        </span>
                        <div
                          className="mt-1 whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{
                            __html: linkify(docketDoc.comment),
                          }}
                        />
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-gray-500">
                        No docket comment recorded.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="relative mt-6 inline-block">
            <button
              type="button"
              onClick={() => setAdvancedExportOpen((open) => !open)}
              aria-expanded={advancedExportOpen}
              aria-controls="export-menu"
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black"
            >
              Export
              <svg
                className={`h-3.5 w-3.5 transition-transform ${advancedExportOpen ? "rotate-180" : ""}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {advancedExportOpen && (
              <div
                id="export-menu"
                className="absolute bottom-[calc(100%+8px)] left-0 z-40 w-[380px] max-h-[80vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl"
              >
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quick export
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={exportToCSV}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-800 transition hover:bg-gray-50"
                  >
                    Export jobs CSV
                  </button>
                  <button
                    onClick={downloadAllPhotos}
                    disabled={zipping}
                    className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition ${
                      zipping
                        ? "cursor-not-allowed border-gray-200 text-gray-400"
                        : "border-gray-200 text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    {zipping ? "Preparing ZIP..." : "Download all photos (ZIP)"}
                  </button>
                  <button
                    onClick={exportTruckChecksPDF}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-800 transition hover:bg-gray-50"
                    title="Export combined pre-start and post-trip PDFs grouped by truck"
                  >
                    Export truck checks PDF
                  </button>
                  <button
                    onClick={exportSummaryPDF}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-800 transition hover:bg-gray-50"
                    title="Export job summary (with photos) as PDF"
                  >
                    Export summary PDF
                  </button>
                </div>

                <div className="mt-4 border-t border-gray-100 pt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Advanced CSV
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-700">From</span>
                      <input
                        type="date"
                        value={exportFromDate}
                        onChange={(e) => setExportFromDate(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-700">To</span>
                      <input
                        type="date"
                        value={exportToDate}
                        onChange={(e) => setExportToDate(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                  </div>

                  <label className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <input
                      type="checkbox"
                      checked={groupExportBySite}
                      onChange={(e) => setGroupExportBySite(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Group export by site</span>
                  </label>

                  <div className="mt-3 mb-2 flex items-center justify-between gap-3">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                      Columns
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedExportColumns(DEFAULT_EXPORT_COLUMNS)}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    >
                      Reset to invoicing default
                    </button>
                  </div>
                  <div className="grid max-h-40 grid-cols-2 gap-1.5 overflow-y-auto pr-1">
                    {EXPORT_COLUMN_OPTIONS.map(([key, label]) => {
                      const checked = selectedExportColumns.includes(key);
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition ${
                            checked
                              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                              : "border-gray-200 bg-white text-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleExportColumn(key)}
                          />
                          <span className="truncate">{label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <button
                    onClick={exportAdvancedCSV}
                    className="mt-3 w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                  >
                    Export advanced CSV
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mt-2">
          {selectedDriverId && date
            ? "No jobs found for the selected driver and date."
            : "Select a driver and date to load jobs."}
        </p>
      )}
    </div>
  );
}
